import type { ReactNode } from 'react';
import type { AutomaticContentBlockProps } from '../types';
import { automaticListLook, theme } from '../theme';

/** The owner's words for the list's cards: this section's word settings (blank ones left out),
 *  plus the Team page's one roster choice (one list, or grouped by office). */
export type AutomaticListWords = Pick<AutomaticContentBlockProps, 'phoneLabel' | 'emailLinkText' | 'profileLinkText' | 'officeLinkText' | 'agentGrouping'>;

/** An automated page's list as the host gives it: drawn already, or drawn from the section's words. */
export type AutomaticContent = ReactNode | ((words: AutomaticListWords) => ReactNode);

/** The word settings on an Automatic list section, and only those. */
export function automaticListWords(props: AutomaticContentBlockProps): AutomaticListWords {
  const out: AutomaticListWords = {};
  for (const k of ['phoneLabel', 'emailLinkText', 'profileLinkText', 'officeLinkText'] as const) {
    const v = typeof props[k] === 'string' ? props[k]!.trim() : '';
    if (v) out[k] = v;
  }
  if (props.agentGrouping === 'office') out.agentGrouping = 'office';
  return out;
}

/** What the Studio's placeholder says when the host names nothing more specific. */
export const AUTOMATIC_CONTENT_PLACEHOLDER = 'This page’s automatic list appears here, kept up to date for you.';

/**
 * Where an automated page's live list sits among its blocks: the agent roster on /agents, the
 * posts on /blog, the community cards on /communities, the homes on /sold and /coming-soon.
 *
 * The block stores only how the list looks. The HOST renders the list (it owns the fetching, paging and gating)
 * and passes it in as `BlockData.automaticContent`; this block only says where it goes, so the
 * broker can write text above and below it and move it in the Studio.
 *
 *   - tenant route: renders `content` exactly where the block is;
 *   - Studio preview (no content, `previewMode`): a placeholder card, so the section can be seen
 *     and selected rather than vanishing;
 *   - anywhere else (a page with no automatic list): nothing.
 *
 * Its settings are how the list looks: icon and link colours, its bands' background and text,
 * Cards per row, Card colour and Card style (theme.ts automaticListLook, painted by blocks.css
 * through the .auto-list-* hooks the lists carry).
 */
export function AutomaticContentBlock({
  content: given,
  previewMode,
  placeholder,
  ...look
}: {
  content?: AutomaticContent;
  previewMode?: boolean;
  placeholder?: string | null;
} & AutomaticContentBlockProps) {
  // A host that draws its list from the section's own words (the Team page's cards) passes a
  // function; the words are this section's settings, so the owner's "Call/Text" reaches the cards.
  const content = typeof given === 'function' ? given(automaticListWords(look)) : given;
  if (content != null && content !== false) {
    // Always one wrapper, so every list follows the site's colours the way listing cards do
    // (data-site-ink, packages/blocks cardStyles.css): written with Tailwind whites and greys, the
    // lists put near-black words on a dark page. The wrapper also carries the list's own settings.
    const { style, cols } = automaticListLook(look);
    return (
      <div data-automatic-list="" data-site-ink="" data-cols={cols} style={style}>
        {content}
      </div>
    );
  }
  if (!previewMode) return null;
  return (
    <section className="px-6 py-12" style={{ fontFamily: theme.bodyFont }} data-automatic-placeholder="">
      <div
        className="mx-auto max-w-5xl rounded-2xl border-2 border-dashed px-8 py-14 text-center"
        style={{ borderColor: theme.accent, backgroundColor: theme.surface, color: theme.text }}
      >
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
          Automatic list
        </p>
        <p className="mx-auto mt-3 max-w-xl text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
          {placeholder?.trim() || AUTOMATIC_CONTENT_PLACEHOLDER}
        </p>
        <p className="mx-auto mt-2 max-w-xl text-sm" style={{ color: theme.textMuted }}>
          Visitors see the live list in this spot. Add sections above or below it, or move it.
        </p>
      </div>
    </section>
  );
}
